<template>
  <div>
    <Head title="Traslados de Inventario entre Depósitos" />

    <!-- Top Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            Traslados de Inventario
          </h1>
          <span v-if="deposit" class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-800 border border-orange-200">
            {{ deposit.name }}
          </span>
          <span v-else class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-800 border border-gray-200">
            🏢 Todos los depósitos
          </span>
        </div>
        <p class="text-sm text-gray-500">
          Control de movimiento de mercancía y equipos entre depósitos, sedes y eventos.
        </p>
      </div>

      <!-- Action buttons -->
      <div class="flex items-center gap-2.5 w-full sm:w-auto">
        <Link
          :href="`/deposits/${effectiveDepositId}/transfer/create`"
          class="px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl flex items-center justify-center gap-2 font-bold text-sm shadow-sm transition-all"
        >
          <i class="fa fa-dolly" />
          <span>Nuevo Traslado</span>
        </Link>
        <Link
          :href="'/inventories'"
          class="px-3.5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl flex items-center justify-center gap-2 font-semibold text-sm shadow-2xs transition-all"
        >
          <i class="fa fa-boxes text-gray-500" />
          <span>Ir a Inventarios</span>
        </Link>
      </div>
    </div>

    <!-- Search & Filter Bar -->
    <div class="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm mb-6 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 max-w-2xl">
        <!-- Search Input -->
        <div class="relative flex-1">
          <i class="fa fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar por referencia, producto, depósito o notas..."
            class="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            @input="onSearchInput"
          />
        </div>

        <!-- Filter Deposit Dropdown -->
        <div class="w-full sm:w-64 shrink-0">
          <select
            v-model="selectedDepositFilter"
            class="w-full px-3.5 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            @change="applyFilter"
          >
            <option value="all">🏢 Todos los depósitos</option>
            <option v-for="dep in deposits" :key="dep.id" :value="dep.id">
              {{ dep.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="text-xs text-gray-400 font-medium whitespace-nowrap self-end md:self-center">
        Total traslados: <strong class="text-gray-900">{{ depositTransfers.total || depositTransfers.data?.length || 0 }}</strong>
      </div>
    </div>

    <!-- Transfers Table -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse text-sm">
          <thead>
            <tr class="bg-gray-50/70 border-b border-gray-200/80 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
              <th class="py-3 px-4">Ref. Traslado</th>
              <th class="py-3 px-4">Fecha</th>
              <th class="py-3 px-4">Origen & Destino</th>
              <th class="py-3 px-4">Productos Trasladados</th>
              <th class="py-3 px-4">Estado</th>
              <th class="py-3 px-4">Observaciones / Evento</th>
              <th class="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-if="!depositTransfers.data || depositTransfers.data.length === 0">
              <td colspan="7" class="py-12 text-center text-gray-400">
                <i class="fa fa-dolly-flatbed text-3xl mb-2 text-gray-300" />
                <p class="font-medium">No se han registrado traslados con los filtros seleccionados.</p>
                <Link
                  :href="`/deposits/${effectiveDepositId}/transfer/create`"
                  class="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs"
                >
                  <i class="fa fa-plus" />
                  <span>Crear Traslado</span>
                </Link>
              </td>
            </tr>
            <tr
              v-for="transfer in depositTransfers.data"
              :key="transfer.id"
              class="hover:bg-orange-50/20 transition-colors"
            >
              <!-- Ref -->
              <td class="py-3.5 px-4 font-mono font-bold text-gray-900 whitespace-nowrap">
                {{ transfer.reference_number || ('#TR-' + transfer.id) }}
              </td>

              <!-- Date -->
              <td class="py-3.5 px-4 whitespace-nowrap text-xs text-gray-600">
                {{ formatDate(transfer.date) }}
              </td>

              <!-- Route (Origin -> Destination) -->
              <td class="py-3.5 px-4">
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-gray-900">{{ transfer.emitter?.name }}</span>
                  <i class="fa fa-arrow-right text-xs text-orange-500" />
                  <span class="font-bold text-orange-700 bg-orange-50 px-2 py-0.5 rounded-lg border border-orange-200 text-xs">
                    {{ transfer.receiver?.name }}
                  </span>
                </div>
                <div v-if="transfer.user" class="text-[11px] text-gray-400 mt-0.5">
                  Por: {{ transfer.user.name || transfer.user.first_name }}
                </div>
              </td>

              <!-- Items Breakdown -->
              <td class="py-3.5 px-4">
                <div v-if="transfer.products && transfer.products.length > 0" class="text-xs space-y-1">
                  <div
                    v-for="item in transfer.products.slice(0, 3)"
                    :key="item.id"
                    class="text-gray-700 flex items-center gap-1.5"
                  >
                    <span class="font-mono text-[10px] text-gray-400">[{{ item.product?.code }}]</span>
                    <span class="font-medium truncate max-w-[200px]">{{ item.product?.name }}</span>
                    <span class="font-bold text-gray-900">({{ item.quantity }} unds)</span>
                  </div>
                  <div v-if="transfer.products.length > 3" class="text-[11px] text-orange-600 font-bold">
                    +{{ transfer.products.length - 3 }} productos más...
                  </div>
                </div>
                <span v-else class="text-xs text-gray-400 italic">Sin ítems</span>
              </td>

              <!-- State -->
              <td class="py-3.5 px-4 whitespace-nowrap">
                <span
                  v-if="transfer.state === 'receive'"
                  class="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-800 border border-blue-200"
                >
                  <i class="fa fa-undo-alt text-[10px] mr-1" /> Reingresado
                </span>
                <span
                  v-else
                  class="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200"
                >
                  <i class="fa fa-check text-[10px] mr-1" /> Trasladado
                </span>
              </td>

              <!-- Notes -->
              <td class="py-3.5 px-4 text-xs text-gray-600 max-w-xs truncate">
                {{ transfer.notes || '-' }}
              </td>

              <!-- Actions -->
              <td class="py-3.5 px-4 text-right whitespace-nowrap">
                <div class="flex items-center justify-end gap-1.5">
                  <!-- Print PDF -->
                  <a
                    :href="`/deposits/transfers/${transfer.id}/pdf`"
                    target="_blank"
                    class="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Imprimir Guía de Traslado (PDF)"
                  >
                    <i class="fa fa-print text-sm" />
                  </a>

                  <!-- Return Merchandise Button -->
                  <button
                    v-if="transfer.state !== 'receive'"
                    class="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                    title="Reingresar / Retornar mercancía al depósito origen"
                    @click="returnMerchandise(transfer)"
                  >
                    <i class="fa fa-undo-alt text-sm" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="depositTransfers.links" class="p-4 border-t border-gray-100 flex justify-end">
        <Pagination :links="depositTransfers.links" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import Swal from 'sweetalert2'

export default {
  name: 'TransferIndex',

  components: {
    Head,
    Link,
    Pagination,
  },

  layout: Layout,

  props: {
    depositTransfers: Object,
    depositId: [Number, String],
    deposit: Object,
    deposits: Array,
    filters: Object,
  },

  data() {
    return {
      searchTerm: this.filters?.search || '',
      selectedDepositFilter: this.filters?.deposit_id || (this.depositId ? String(this.depositId) : 'all'),
      searchDebounceTimer: null,
    }
  },

  computed: {
    effectiveDepositId() {
      if (this.selectedDepositFilter && this.selectedDepositFilter !== 'all') {
        return this.selectedDepositFilter
      }
      if (this.depositId && this.depositId !== 'all') return this.depositId
      if (this.deposit && this.deposit.id) return this.deposit.id
      if (this.deposits && this.deposits.length > 0) return this.deposits[0].id
      return 5
    },
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const parts = dateStr.split('T')[0].split('-')
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`
      return dateStr
    },

    onSearchInput() {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.applyFilter()
      }, 350)
    },

    applyFilter() {
      const query = {}
      if (this.searchTerm && this.searchTerm.trim() !== '') {
        query.search = this.searchTerm
      }
      if (this.selectedDepositFilter && this.selectedDepositFilter !== 'all') {
        query.deposit_id = this.selectedDepositFilter
      } else {
        query.deposit_id = 'all'
      }

      this.$inertia.get('/deposits/transfers/all', query, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    },

    returnMerchandise(transfer) {
      Swal.fire({
        title: '¿Reingresar mercancía al origen?',
        html: `Esta acción descontará el stock de <b>${transfer.receiver?.name}</b> y lo sumará de nuevo a <b>${transfer.emitter?.name}</b>.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, reingresar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ea580c',
      }).then(result => {
        if (result.isConfirmed) {
          this.$inertia.post(`/deposits/transfers/${transfer.id}/return`, {}, {
            preserveScroll: true,
            onSuccess: () => {
              Swal.fire({
                icon: 'success',
                title: 'Mercancía reingresada',
                text: 'El inventario ha vuelto al depósito origen.',
                timer: 2000,
                showConfirmButton: false,
              })
            },
            onError: (err) => {
              Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err.error || 'No se pudo reingresar la mercancía',
              })
            },
          })
        }
      })
    },
  },
}
</script>
